import { runRenderPipeline } from 'modules/manager/domain/render';
import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import { selectActiveTemplate, selectError } from 'modules/manager/store';
import { useGetRenderContextQuery } from 'modules/manager/store/render-api';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import {
  HeadlessRenderCanvas,
  RenderErrorBoundary,
  StatusOverlay,
  collectBgUrls,
  loadCanvasJson,
  makeStatusTitle,
  nextFrame,
  parseId,
  toUserMessage,
} from './render-shared';
import { useRenderPageState } from './use-render-page-state';

const statusTitle = makeStatusTitle({
  loading: 'Loading template and match data…',
  rendering: 'Rendering PNG…',
});

const TemplateRenderPageInner: React.FC = () => {
  const [params] = useSearchParams();

  const matchIds = useMemo(() => collectMatchIds(params), [params]);
  // URL contract: ?templateId=N (was: ?boardId=N — kept as-is for backwards compat with external callers
  // by also reading the legacy `boardId` query key when the new one is absent).
  const templateId = parseId(params.get('templateId') ?? params.get('boardId'));
  const bgUrls = useMemo(() => collectBgUrls(params), [params]);

  const activeTemplate = useAppSelector(selectActiveTemplate);
  const loadError = useAppSelector(selectError);
  const ctxQuery = useGetRenderContextQuery(matchIds.length > 0 ? { matchIds } : skipToken);

  const { editor, setEditor, status, setStatus, errorMessage, setErrorMessage, links, setLinks, orchestrated } = useRenderPageState({
    templateId,
    missingParamMessage: matchIds.length === 0 ? 'Missing matchId in URL' : null,
    contextError: ctxQuery.error,
    contextErrorFallback: 'Failed to load render context',
  });

  useEffect(() => {
    if (orchestrated.current) return;
    if (!editor || !activeTemplate || !ctxQuery.data) return;
    if (loadError) {
      console.error(loadError);
      setStatus('error');
      setErrorMessage(loadError);
      return;
    }
    orchestrated.current = true;
    setStatus('rendering');

    (async () => {
      try {
        await loadCanvasJson(editor, activeTemplate.version, activeTemplate.objects ?? []);
        await runRenderPipeline(editor, ctxQuery.data!, {
          bgUrls,
          templateType: activeTemplate.templateType,
          templateSubtypes: activeTemplate.templateSubtypes,
          layout: { settings: activeTemplate.matchLayout, authoredObjects: activeTemplate.objects ?? [] },
        });
        await nextFrame();
        const dataUrl = editor.canvas.toDataURL({ format: 'png', multiplier: 1 });
        const saved = await mediaManagerRepository.persistRenderedImage({
          matchId: matchIds[0],
          templateId: activeTemplate.id,
          dataUrl,
        });
        setLinks([{ url: saved.imageUrl, label: `Match ${saved.matchId ?? matchIds[0]}` }]);
        setStatus('done');
      } catch (err) {
        console.error(err);
        setStatus('error');
        setErrorMessage(toUserMessage(err));
      }
    })();
  }, [editor, activeTemplate, ctxQuery.data, loadError, bgUrls, orchestrated, setStatus, setErrorMessage, setLinks]);

  if (!activeTemplate) {
    return <StatusOverlay status={status} message={errorMessage ?? 'Loading template…'} title={statusTitle} />;
  }

  return (
    <>
      <HeadlessRenderCanvas template={activeTemplate} editor={editor} onReady={setEditor} />
      <StatusOverlay status={status} message={errorMessage} title={statusTitle} links={links} />
    </>
  );
};

// URL contract for multi-match templates: ?matchId1=A&matchId2=B&matchId3=C
// (1-based, mirrors the bg1..bgN pattern). Single-match templates fall back
// to ?matchId=N. Order is preserved: matches[i-1] resolves placeholders with
// matchNumber=i.
function collectMatchIds(params: URLSearchParams): number[] {
  const out: number[] = [];
  for (let i = 1; i <= 50; i++) {
    const raw = params.get(`matchId${i}`);
    if (raw === null) {
      if (i === 1) continue;
      break;
    }
    const id = parseId(raw);
    if (id !== null) out.push(id);
  }
  if (out.length > 0) return out;
  const legacy = parseId(params.get('matchId'));
  return legacy !== null ? [legacy] : [];
}

const TemplateRenderPage: React.FC = () => (
  <RenderErrorBoundary>
    <TemplateRenderPageInner />
  </RenderErrorBoundary>
);

export default TemplateRenderPage;
